// Furniture that is not a chair (CIN-15126): the double bed of a bed cinema and the beanbag. Like the seat
// styles, every piece is a handful of boxes in the seat's local space (meters, origin at the base center,
// facing -z, the head end at +z), so it instances into the one rounded unit box every seat part shares, in
// the studio and on the guest map alike. A bed is two places: each half is modeled on its own (its pillow,
// its console on the outer side) and the two halves meet flush, so a pair reads as one bed while each half
// keeps its own color and click target. Pure module: relative imports only.
import type { Box, Vec3 } from '../seat-map-3d/scene-geometry.ts';

import type { SeatArmrests } from './seat-styles.ts';

/** Seats that are not drawn in the room's seat style. */
export type FurnitureKind = 'bed' | 'beanbag';

/** Parts in the seat color, plus the strips of the warm underglow (drawn in the accent color). */
export type FurnitureParts = { parts: Box[]; glows: Box[] };

const vec = (x: number, y: number, z: number): Vec3 => ({ x, y, z });
const box = (center: Vec3, size: Vec3, tiltX?: number): Box =>
    tiltX === undefined ? { center, size } : { center, size, tiltX };

/** Share of the half's width the side console takes. */
const CONSOLE_SHARE = 0.16;
/** Height of the frame and of the mattress on it, as shares of the bed's height (the hood's top). */
const FRAME_SHARE = 0.26;
const MATTRESS_SHARE = 0.14;
const HOOD_THICKNESS = 0.06;
const HOOD_SEGMENTS = 4;
/** How far the hood bends forward over the pillow, from vertical at the head end. */
const HOOD_SWEEP = (2 * Math.PI) / 3;
const GLOW_SIZE = 0.025;

/** Top of the mattress above the base, in meters: where a guest lies. */
export function bedMattressTop(h: number): number {
    return h * (FRAME_SHARE + MATTRESS_SHARE);
}

/**
 * The padded shell at the head end: a back panel and an arc of tilted slabs that curves up and forward over
 * the pillow. `width` is the shell's width, centered on x = 0.
 */
function hoodParts(width: number, h: number, d: number): Box[] {
    const radius = Math.min(0.45, d * 0.22, h * 0.4);
    const backZ = d / 2 - HOOD_THICKNESS / 2;
    const springY = h - radius;
    const parts: Box[] = [box(vec(0, springY / 2 + h * 0.06, backZ), vec(width, springY - h * 0.12, HOOD_THICKNESS))];
    const centerZ = backZ - radius;
    const step = HOOD_SWEEP / HOOD_SEGMENTS;
    // A slab of the arc: its long side follows the tangent, slightly longer than the chord so neighbors overlap.
    const length = 2 * radius * Math.sin(step / 2) + HOOD_THICKNESS;
    for (let k = 0; k < HOOD_SEGMENTS; k++) {
        const angle = (k + 0.5) * step;
        parts.push(
            box(
                vec(0, springY + radius * Math.sin(angle), centerZ + radius * Math.cos(angle)),
                vec(width, length, HOOD_THICKNESS),
                -angle
            )
        );
    }
    return parts;
}

/**
 * One half of a double bed: frame, mattress, pillow and the hood over the head end; a console with the seat
 * number and cupholders on every side in `sides` (the outer sides; a paired half has none toward its partner,
 * where its mattress meets the other one flush). A lone half gets both.
 */
export function bedParts(w: number, h: number, d: number, sides: SeatArmrests): FurnitureParts {
    const consoleWidth = w * CONSOLE_SHARE;
    const left = -w / 2 + (sides.left ? consoleWidth : 0);
    const right = w / 2 - (sides.right ? consoleWidth : 0);
    const frameTop = h * FRAME_SHARE;
    const mattressTop = bedMattressTop(h);
    const mattressDepth = d * 0.84;
    const parts: Box[] = [
        box(vec(0, frameTop / 2, 0), vec(w, frameTop, d * 0.96)),
        box(
            vec((left + right) / 2, (frameTop + mattressTop) / 2, -d / 2 + d * 0.03 + mattressDepth / 2),
            vec(right - left, mattressTop - frameTop, mattressDepth)
        ),
        box(vec((left + right) / 2, mattressTop + h * 0.045, d * 0.3), vec((right - left) * 0.7, h * 0.09, d * 0.13)),
        ...hoodParts(w, h, d)
    ];
    const glows: Box[] = [box(vec(0, GLOW_SIZE / 2, -d * 0.48 - GLOW_SIZE / 2), vec(w * 0.92, GLOW_SIZE, GLOW_SIZE))];
    for (const side of ['left', 'right'] as const) {
        if (!sides[side]) continue;
        const sign = side === 'left' ? -1 : 1;
        const x = sign * (w / 2 - consoleWidth / 2);
        // The console runs along the head half of the bed; the cheek closes the hood on that side.
        parts.push(box(vec(x, h * 0.36, d * 0.08), vec(consoleWidth * 0.92, h * 0.2, d * 0.5)));
        parts.push(
            box(vec(sign * (w / 2 - HOOD_THICKNESS / 2), h * 0.62, d * 0.36), vec(HOOD_THICKNESS, h * 0.72, d * 0.26))
        );
        glows.push(box(vec(sign * (w * 0.48 + GLOW_SIZE / 2), GLOW_SIZE / 2, 0), vec(GLOW_SIZE, GLOW_SIZE, d * 0.9)));
    }
    return { parts, glows };
}

/** The filler between two bed halves that do not touch: frame, mattress and hood, no pillow and no consoles. */
export function bedBridgeParts(width: number, h: number, d: number): FurnitureParts {
    const frameTop = h * FRAME_SHARE;
    const mattressTop = bedMattressTop(h);
    const mattressDepth = d * 0.84;
    return {
        parts: [
            box(vec(0, frameTop / 2, 0), vec(width, frameTop, d * 0.96)),
            box(
                vec(0, (frameTop + mattressTop) / 2, -d / 2 + d * 0.03 + mattressDepth / 2),
                vec(width, mattressTop - frameTop, mattressDepth)
            ),
            ...hoodParts(width, h, d)
        ],
        glows: [box(vec(0, GLOW_SIZE / 2, -d * 0.48 - GLOW_SIZE / 2), vec(width, GLOW_SIZE, GLOW_SIZE))]
    };
}

/** A beanbag: a fat base, a sagging seat pocket and a back that leans away; the rounding makes it a blob. */
export function beanbagParts(w: number, h: number, d: number): FurnitureParts {
    return {
        parts: [
            box(vec(0, h * 0.2, 0), vec(w, h * 0.4, d)),
            box(vec(0, h * 0.42, -d * 0.12), vec(w * 0.88, h * 0.24, d * 0.62)),
            box(vec(0, h * 0.62, d * 0.22), vec(w * 0.84, h * 0.66, d * 0.42), 0.3)
        ],
        glows: []
    };
}

/** Parts of a furniture piece; `sides` only matters for a bed half. */
export function furnitureParts(
    kind: FurnitureKind,
    w: number,
    h: number,
    d: number,
    sides: SeatArmrests
): FurnitureParts {
    return kind === 'bed' ? bedParts(w, h, d, sides) : beanbagParts(w, h, d);
}

/**
 * Where the eye of a guest is, relative to the seat's base center: height above the base and how far forward
 * (along the seat's facing) of the center. On a bed the head rests on the pillow, far back and low.
 */
export function furnitureEye(kind: FurnitureKind, size: Vec3): { height: number; forward: number } {
    if (kind === 'bed') return { height: bedMattressTop(size.y) + 0.35, forward: -size.z * 0.26 };
    return { height: 0.75, forward: 0 };
}
